import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import _ from 'lodash';
import {CardEligibility, CardHolder, CardInformation, CardRequest, CardSignatory} from '../../../@types/CardSubscriptionTypes';
import {PartialStateRequest} from '../../../../@types/utils';
import {CardType} from '../../../@types/cardTypes';

export type CardSubscriptionState = {
  formData: CardRequest;
};

const initialState: CardSubscriptionState = {
  formData: {

    eligibility: {
      cardHolderBelongsToContractUser: false
    },
    information: {},
    holder: {},
    signatories: [
      {}
    ]
  } as CardRequest
};


type CardSubscriptionRequest<T extends keyof CardRequest> = PartialStateRequest<CardRequest, T>;

const DEBIT_CARD_LIMIT = '2500';

const cardSubscriptionSlice = createSlice({
  initialState,
  name: 'cardSubscription',
  reducerPath: 'cardSubscription',
  reducers: {
    setGlobalFormData(state, action: PayloadAction<CardRequest>) {
      state.formData = action.payload;
    },

    setFormData<T extends keyof CardRequest>(state: CardSubscriptionState, action: PayloadAction<CardSubscriptionRequest<T>>) {
      _.set(state.formData, action.payload.field, action.payload.value);
    },

    setEligibilityFieldFormData<T extends keyof CardEligibility>(state: CardSubscriptionState, action: PayloadAction<PartialStateRequest<CardEligibility, T>>) {
      _.set(state.formData.eligibility, action.payload.field, action.payload.value);
    },

    setInformationFieldFormData<T extends keyof CardInformation>(state: CardSubscriptionState, action: PayloadAction<PartialStateRequest<CardInformation, T>>) {
      _.set(state.formData.information, action.payload.field, action.payload.value);
    },

    setHolderFieldFormData<T extends keyof CardHolder>(state: CardSubscriptionState, action: PayloadAction<PartialStateRequest<CardHolder, T>>) {
      _.set(state.formData.holder, action.payload.field, action.payload.value);
    },

    setFirstSignatoryFieldFormData<T extends keyof CardSignatory>(state: CardSubscriptionState, action: PayloadAction<PartialStateRequest<CardSignatory, T>>) {
      _.set(state.formData.signatories[0], action.payload.field, action.payload.value);
    },


    addSecondSignatory(state: CardSubscriptionState) {
      const cardSignatories = state.formData.signatories;
      if (cardSignatories?.length < 2) {
        cardSignatories.push({email: '', hasValidLuxtrust: false});
      }
    },

    setSecondSignatoryFieldFormData<T extends keyof CardSignatory>(state: CardSubscriptionState, action: PayloadAction<PartialStateRequest<CardSignatory, T>>) {
      if (!state.formData.signatories[1]) {
        state.formData.signatories.push({email: ''});
      }

      _.set(state.formData.signatories[1], action.payload.field, action.payload.value);
    },

    removeSecondSignatory(state: CardSubscriptionState) {
      const cardSignatories = state.formData.signatories;
      if (cardSignatories.length > 1) {
        cardSignatories.pop();
      }
    },


    initializeState(state: CardSubscriptionState, action: PayloadAction<{ contractReference: string, type: CardType, primaryContract: string }>) {
      _.set(state.formData, 'type', action.payload.type);
      _.set(state.formData, 'primaryContract', action.payload.primaryContract);
      _.set(state.formData, 'contractReference', action.payload.contractReference);
      if (action.payload.type === 'VISA_DEBIT'){
        _.set(state.formData, 'information.monthlyUsageLimitAmount', DEBIT_CARD_LIMIT);
      }
    },

    reset: () => initialState
  }
});

export const {
  setGlobalFormData,
  setFormData,
  setEligibilityFieldFormData,
  setInformationFieldFormData,
  setHolderFieldFormData,
  setFirstSignatoryFieldFormData,
  setSecondSignatoryFieldFormData,
  addSecondSignatory,
  removeSecondSignatory,
  initializeState,
  reset
} = cardSubscriptionSlice.actions;
export const reducer = cardSubscriptionSlice.reducer;
